import React, { useEffect, useState } from 'react';
import {
    DollarSign,
    Package,
    Truck,
    TrendingUp,
    AlertTriangle,
    ChevronRight,
    Shield,
    Shirt,
    RefreshCw,
    CheckCircle2,
    MapPin,
    Plus,
    Clock,
    FileBarChart
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { cn } from '../../components/ui/utils';
import { Badge } from '../../components/ui/badge';
import { toSydneyDate } from '../../utils/helpers';

interface DashboardStats {
    inventoryValue: number;
    totalStock: number;
    uniformStock: number;
    ppeStock: number;
    incomingCount: number;
    incomingValue: number;
    monthIssuancesCount: number;
    monthIssuancesValue: number;
}

interface TopItem {
    name: string;
    SKU: string;
    issued: number;
    value: number;
    iconType: 'ppe' | 'uniform';
}

interface LowStockAlert {
    name: string;
    current: number;
    target: number;
    percentage: number;
}

interface ReplacementAlert {
    id: string;
    inventoryItem: {
        name: string;
        sku: string;
    };
    issuance: {
        location: { name: string };
        systemUser?: { name: string };
        fieldWorker?: { name: string };
    };
    expiryDate: string;
}

interface InventoryDashboardProps {
    onNavigateReports?: (view: 'main' | 'worker' | 'item' | 'location' | 'cost' | 'replacement') => void;
}

const InventoryDashboard: React.FC<InventoryDashboardProps> = ({ onNavigateReports }) => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [topItems, setTopItems] = useState<TopItem[]>([]);
    const [lowStock, setLowStock] = useState<LowStockAlert[]>([]);
    const [replacementAlerts, setReplacementAlerts] = useState<ReplacementAlert[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsData, topItemsData, lowStockData, alertsData] = await Promise.all([
                    api.get<DashboardStats>('/api/inventory/dashboard/stats'),
                    api.get<TopItem[]>('/api/inventory/dashboard/top-issued'),
                    api.get<LowStockAlert[]>('/api/inventory/dashboard/low-stock'),
                    api.get<ReplacementAlert[]>('/api/inventory/issuances/alerts')
                ]);
                setStats(statsData);
                setTopItems(topItemsData);
                setLowStock(lowStockData);
                setReplacementAlerts(alertsData);
            } catch (error) {
                console.error('Error fetching dashboard data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="h-32 bg-gray-100 animate-pulse rounded-xl" />
            ))}
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Card 1: Total Value */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Total Inventory Value</p>
                            <h3 className="text-xl sm:text-2xl font-bold">${stats?.inventoryValue.toLocaleString()}</h3>
                        </div>
                        <div className="p-2 sm:p-3 bg-green-50 rounded-xl">
                            <DollarSign className="text-green-600" size={20} />
                        </div>
                    </div>
                </div>

                {/* Card 2: Items in Stock */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Items in Stock</p>
                            <h3 className="text-xl sm:text-2xl font-bold">{stats?.totalStock}</h3>
                            <div className="flex gap-2 mt-2 text-[10px] sm:text-xs font-medium">
                                <span className="text-blue-600">Uniform: {stats?.uniformStock}</span>
                                <span className="text-orange-600">PPE: {stats?.ppeStock}</span>
                            </div>
                        </div>
                        <div className="p-2 sm:p-3 bg-blue-50 rounded-xl">
                            <Package className="text-blue-600" size={20} />
                        </div>
                    </div>
                </div>

                {/* Card 3: Incoming Inventory */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Incoming Inventory</p>
                            <h3 className="text-xl sm:text-2xl font-bold">{stats?.incomingCount}</h3>
                            <p className="text-[10px] sm:text-xs text-gray-400 mt-2 font-medium">Value: ${stats?.incomingValue.toLocaleString()}</p>
                        </div>
                        <div className="p-2 sm:p-3 bg-purple-50 rounded-xl">
                            <Truck className="text-purple-600" size={20} />
                        </div>
                    </div>
                </div>

                {/* Card 4: Monthly Issuance */}
                <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-500 mb-1">This Month</p>
                            <h3 className="text-xl sm:text-2xl font-bold">{stats?.monthIssuancesCount}</h3>
                            <p className="text-[10px] sm:text-xs text-gray-400 mt-2 font-medium">Value: ${stats?.monthIssuancesValue.toLocaleString()}</p>
                        </div>
                        <div className="p-2 sm:p-3 bg-orange-50 rounded-xl">
                            <TrendingUp className="text-orange-600" size={20} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Issued Items */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-6">
                        <Package size={20} className="text-gray-400" />
                        <h4 className="font-bold text-gray-800">Top Issued Items</h4>
                    </div>
                    <div className="space-y-4">
                        {topItems.length === 0 ? (
                            <div className="py-10 text-center text-gray-400">
                                <TrendingUp size={40} className="mx-auto mb-3 opacity-20" />
                                <p className="font-medium text-sm">No items have been issued yet.</p>
                                <p className="text-[10px] uppercase font-bold tracking-tighter mt-1">Start by processing a new issuance</p>
                            </div>
                        ) : (
                            topItems.map((item, index) => (
                                <div key={index} className="flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group">
                                    <div className="bg-gray-50 text-[10px] font-black text-gray-400 w-7 h-7 flex items-center justify-center rounded-lg mr-2 shrink-0 group-hover:bg-white transition-colors border border-transparent group-hover:border-gray-100">
                                        #{index + 1}
                                    </div>
                                    <div className="flex-1 flex items-center gap-4 min-w-0">
                                        <div className={cn(
                                            "p-2.5 rounded-xl shrink-0",
                                            item.iconType === 'ppe' ? "bg-orange-50" : "bg-blue-50"
                                        )}>
                                            {item.iconType === 'ppe' ? <Shield size={18} className="text-orange-500" /> : <Shirt size={18} className="text-blue-500" />}
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-bold text-sm text-gray-700 truncate">{item.name}</p>
                                            <p className="text-xs text-gray-400 font-medium tracking-wide">
                                                SKU: {item.SKU}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right w-24 shrink-0">
                                        <p className="text-sm font-bold text-gray-700">{item.issued} issued</p>
                                        <p className="text-xs text-gray-400 font-medium">${item.value.toLocaleString()}</p>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Low Stock Alert */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 mb-6">
                        <AlertTriangle size={20} className="text-orange-400" />
                        <h4 className="font-bold text-gray-800">Low Stock Alert</h4>
                    </div>
                    <div className="space-y-6">
                        {lowStock.length === 0 ? (
                            <div className="py-10 text-center text-gray-400">
                                <CheckCircle2 size={40} className="mx-auto mb-3 opacity-20 text-green-500" />
                                <p className="font-medium text-sm text-gray-500">Stock levels are healthy.</p>
                                <p className="font-medium text-sm">All items are above their thresholds</p>
                            </div>
                        ) : (
                            lowStock.map((alert, index) => (
                                <div key={index} className="space-y-2">
                                    <div className="flex justify-between items-end">
                                        <div className="flex items-center gap-4">
                                            <div className="p-2.5 bg-orange-50 rounded-xl">
                                                <Shield size={18} className="text-orange-500" />
                                            </div>
                                            <div>
                                                <p className="font-bold text-sm text-gray-700">{alert.name}</p>
                                                <p className="text-xs text-gray-400 font-medium">{alert.current} of {alert.target}</p>
                                            </div>
                                        </div>
                                        <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">{alert.percentage}%</span>
                                    </div>
                                    <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                                        <div
                                            className="bg-slate-900 h-full rounded-full transition-all duration-1000"
                                            style={{ width: `${alert.percentage}%` }}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* Replacement Alerts Section */}
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <RefreshCw size={20} className="text-blue-400" />
                        <h4 className="font-bold text-gray-800">Replacement & Expiry Alerts</h4>
                        <Badge className="bg-blue-50 text-blue-600 border-0 rounded-full font-bold ml-2">
                            {replacementAlerts.length}
                        </Badge>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onNavigateReports?.('replacement')}
                        className="flex items-center gap-2 text-blue-600 border-blue-100 hover:bg-blue-50 hover:text-blue-700 font-bold transition-all"
                    >
                        <FileBarChart size={16} />
                        View Detailed Report
                    </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {replacementAlerts.length === 0 ? (
                        <div className="col-span-full py-12 text-center text-gray-400">
                            <CheckCircle2 size={40} className="mx-auto mb-3 opacity-20 text-green-500" />
                            <p className="font-medium text-sm">All equipment is currently within its operational lifespan.</p>
                        </div>
                    ) : (
                        replacementAlerts.slice(0, 6).map((alert) => {
                            const isExpired = new Date(alert.expiryDate) < new Date();
                            return (
                                <div key={alert.id} className={cn(
                                    "p-4 rounded-2xl border transition-all hover:shadow-md group",
                                    isExpired ? "bg-red-50/30 border-red-100" : "bg-gray-50/50 border-gray-100"
                                )}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="p-2 bg-white rounded-xl shadow-sm border border-gray-100 group-hover:scale-110 transition-transform">
                                            <Shield size={20} className={isExpired ? "text-red-500" : "text-blue-500"} />
                                        </div>
                                        <Badge variant="outline" className={cn(
                                            "rounded-lg border-0 font-bold text-[10px] uppercase tracking-wide",
                                            isExpired ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"
                                        )}>
                                            {isExpired ? 'Expired' : 'Nearing End'}
                                        </Badge>
                                    </div>

                                    <div className="space-y-1 mb-4">
                                        <p className="font-bold text-gray-900 line-clamp-1">{alert.inventoryItem.name}</p>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{alert.inventoryItem.sku}</p>
                                    </div>

                                    <div className="space-y-2 pt-3 border-t border-gray-100/50">
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-gray-400 border border-gray-100">
                                                <MapPin size={10} />
                                            </div>
                                            <span className="text-xs font-bold text-gray-600">{alert.issuance.location.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-gray-400 border border-gray-100">
                                                <Plus size={10} />
                                            </div>
                                            <span className="text-xs font-bold text-gray-600">
                                                {alert.issuance.systemUser?.name || alert.issuance.fieldWorker?.name || 'Unknown Worker'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-gray-400 border border-gray-100">
                                                <Clock size={10} />
                                            </div>
                                            <span className={cn("text-xs font-bold", isExpired ? "text-red-600" : "text-orange-600")}>
                                                Due: {toSydneyDate(alert.expiryDate)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {replacementAlerts.length > 6 && (
                    <div className="mt-8 pt-6 border-t border-gray-100 text-center">
                        <p className="text-sm text-gray-500 mb-4 font-medium">
                            Showing 6 of {replacementAlerts.length} active alerts.
                        </p>
                        <Button
                            variant="ghost"
                            onClick={() => onNavigateReports?.('replacement')}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-bold gap-2"
                        >
                            View All Replacement Alerts
                            <ChevronRight size={16} />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InventoryDashboard;
